import { RefObject, useLayoutEffect, useRef, useState } from "react";

// Measures a dropdown panel after it opens and flips it to the right edge of
// its trigger (instead of the left edge) when it would otherwise cross a
// boundary. By default the boundary is the viewport's right edge. Pass
// `boundaryRef` pointing at a trailing element (e.g. a "Save"/"Export" button
// pinned to the end of the same toolbar row with `ml-auto`) with
// `boundaryEdge="left"` when the dropdown could fit on screen but would still
// overlap that neighbor — the check then compares against that element's
// left edge instead of the viewport's right edge.
export function useEdgeAlignedDropdown<T extends HTMLElement = HTMLDivElement>(
    isOpen: boolean,
    boundaryRef?: RefObject<HTMLElement>,
    boundaryEdge: "left" | "right" = "right"
) {
    const ref = useRef<T>(null);
    const [alignRight, setAlignRight] = useState(false);

    useLayoutEffect(() => {
        if (!isOpen || !ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const boundaryRect = boundaryRef?.current?.getBoundingClientRect();
        const boundary = boundaryRect
            ? (boundaryEdge === "left" ? boundaryRect.left : boundaryRect.right)
            : window.innerWidth;
        setAlignRight(rect.right > boundary);
    }, [isOpen, boundaryRef, boundaryEdge]);

    return { ref, alignRight, alignClass: alignRight ? "right-0" : "left-0" };
}
